import { Box, Flex } from '@chakra-ui/react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Socket } from 'phoenix'
import config from 'configs/app';
import HeroBanner from 'ui/home/HeroBanner';
import ChainIndicators from 'ui/home/indicators/ChainIndicators';
import LatestArbitrumL2Batches from 'ui/home/latestBatches/LatestArbitrumL2Batches';
import LatestZkEvmL2Batches from 'ui/home/latestBatches/LatestZkEvmL2Batches';
import LatestBlocks from 'ui/home/LatestBlocks';
import NextBlockFrags from 'ui/home/NextBlockFrags';
import Stats from 'ui/home/Stats';
import Transactions from 'ui/home/Transactions';
import AdBanner from 'ui/shared/ad/AdBanner';
import { Fragment } from 'types/views/fragment';

const rollupFeature = config.features.rollup;

const Home = () => {
    const [fragData, setFragData] = useState<Fragment | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');

    const formatTimestamp = useCallback((timestamp: number) => {
        const date = new Date(timestamp);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
        return `${hours}:${minutes}:${seconds}.${milliseconds}`;
    }, []);

    useEffect(() => {
        const newSocket = new Socket(`${config.app.fragsWebSocketUrl}`);
        newSocket.connect();

        const newChannel = newSocket.channel("fragments:new");

        newChannel.join()
            .receive("ok", () => {
                setConnectionStatus('connected');
            })
            .receive("error", () => {
                setConnectionStatus('disconnected');
            })

        newChannel.on("new_frag", payload => {
            setFragData(payload)
        });

        return () => {
            newChannel.leave();
            newSocket.disconnect();
            setConnectionStatus('disconnected');
        }
    }, [])

    const leftWidget = (() => {
        if (rollupFeature.isEnabled && !rollupFeature.homepage.showLatestBlocks) {
            switch (rollupFeature.type) {
                case 'zkEvm':
                    return <LatestZkEvmL2Batches />;
                case 'arbitrum':
                    return <LatestArbitrumL2Batches />;
            }
        }

        return (
            <Flex flexDir="column" gap={2}>
                <NextBlockFrags
                    fragData={fragData}
                    connectionStatus={connectionStatus}
                    formatTimestamp={formatTimestamp}
                />
                <LatestBlocks />
            </Flex>
        );
    })();

    const currentTxData = fragData?.data?.txs?.length ? fragData.data.txs.map(tx => {
        tx.blockNumber = fragData.blockNumber;
        tx.timestamp = fragData.timestamp;
        return tx
    }) : [];

    return (
        <Box as="main">
            <HeroBanner />
            <Flex flexDir={{ base: 'column', lg: 'row' }} columnGap={2} rowGap={1} mt={3} _empty={{ mt: 0 }}>
                <Stats />
                <ChainIndicators />
            </Flex>
            <AdBanner mt={6} mx="auto" display={{ base: 'flex', lg: 'none' }} justifyContent="center" />
            <Flex mt={8} direction={{ base: 'column', lg: 'row' }} columnGap={12} rowGap={6}>
                {leftWidget}
                <Box flexGrow={1}>
                    <Transactions txData={currentTxData} />
                </Box>
            </Flex>
        </Box>
    );
};

export default Home;